/**
 * Stande brand palette — the foundation of the design system.
 *
 * The values below are the source of truth, taken from the Figma
 * design-system page (node ids in brackets). Nothing may invent a colour.
 *
 * Figma names two different frames "stande-grey" (cream and #f1f1f1). The
 * lighter one is the page ground everywhere in the product, so it is `cream`
 * here and the neutral one keeps `grey`, matching how they are spoken about.
 * Cream moved from the warm #fbfaf6 to #fbfbfb (Oct 2026).
 */
export const STANDE = {
  black: "#222222",           // 204:305  stande-black
  secondaryOrange: "#ff4400", // 114:809  stande-secondary-orange
  primaryBlue: "#02c9fe",     // 114:811  stande-primary-blue
  cream: "#fbfbfb",           // 114:810  stande-cream
  grey: "#f1f1f1",            // 114:817  stande-grey (neutral)
  greyLight: "#fbfbfb",       //          stande-grey-light
  lightRed: "#ffedec",        // 114:813  stande-light-red
  lightYellow: "#fff5e4",     // 114:815  stande-light-yellow
  lightGreen: "#f0ffee",      // 221:59   stande-light-green
  // The tints above are grounds for dark text. These are the same three
  // meanings at full strength, for when a mark has to read at 6px.
  darkRed: "#ff4a4a",         //          stande-dark-red
  darkGreen: "#0dcf07",       //          stande-dark-green
  darkYellow: "#ecc30a",      //          stande-dark-yellow
};

/**
 * A UI needs more steps than eight — borders and secondary text among them.
 * Rather than inventing new hues, the ramp is a straight neutral line between
 * the two grey tokens and stande-black. Every step is pure grey: no purple,
 * no blue cast. `100` and `900` ARE the tokens.
 */
export const GREY = {
  50: STANDE.cream,   // #fbfbfb the ground
  100: STANDE.grey,   // #f1f1f1 the token
  200: "#e4e4e4",     // hairlines and borders
  300: "#cfcfcf",     // disabled edges
  400: "#9a9a9a",     // muted text
  600: "#5c5c5c",     // secondary text
  800: "#3a3a3a",
  900: STANDE.black,  // #222222 the token
};

/** Dark mode, on the same neutral line. stande-black becomes a surface. */
export const GREY_DARK = {
  page: "#121212",
  surface: "#1a1a1a",
  subtle: STANDE.black, // #222222
  border: "#2e2e2e",
  hover: "#262626",
  text: "#f5f5f5",
  textSub: "#b4b4b4",
  textMuted: "#8a8a8a",
};

/**
 * The light tints are built for dark text on a pale ground; on a near-black
 * surface they read as white blocks. Each gets a counterpart doing the same
 * job. The brand hues themselves only lift enough to hold up on dark.
 */
export const STANDE_DARK = {
  black: GREY_DARK.text,
  secondaryOrange: "#ff6a44",
  primaryBlue: "#5bd9ff",
  cream: GREY_DARK.surface,
  grey: GREY_DARK.subtle,
  // The lightest step off the page in light mode; the lightest step off the
  // page in dark mode is a shade up from the surface, not down.
  greyLight: "#1f1f1f",
  lightRed: "#3a2220",
  lightYellow: "#33291a",
  lightGreen: "#1b2f1e",
  // Lifted just enough to hold up on a near-black ground without glowing.
  darkRed: "#ff6b6b",
  darkGreen: "#3ee038",
  darkYellow: "#f0cf3d",
};

/** Text that sits on each tint, so a caller never has to guess. */
export const ON_TINT = {
  light: {
    lightRed: "#b3261e",
    lightYellow: "#8a5a00",
    lightGreen: "#1f7a3d",
    primaryBlue: "#045a73",
  },
  dark: {
    lightRed: "#ff9f92",
    lightYellow: "#f2c471",
    lightGreen: "#86e29a",
    primaryBlue: "#9ae9fe",
  },
};

export const brand = (isDark) => (isDark ? STANDE_DARK : STANDE);
export const onTint = (isDark) => (isDark ? ON_TINT.dark : ON_TINT.light);
